// src/components/error/AppErrorBoundary.tsx
//
// Top-level error boundary. Without this, a single render error
// anywhere in the tree white-screens the whole app — bad for
// users, worse for diagnosing what went wrong (stack traces only
// land in console, never on screen).
//
// Boundary lives ABOVE the router and gates so a crash in any
// route still gets caught. The fallback shows:
//   - A short, friendly message
//   - A "Try again" button that resets the boundary
//   - A "Reload" button as a hard reset
//   - The error message in a small details block (devs / power
//     users; collapsed by default)
//
// We don't ship a logger here — wire your error reporter
// (Sentry, etc.) into `componentDidCatch` if you want server-side
// visibility. The default just console.errors so dev workflows
// keep working.

import React from 'react';
import { Box, Typography, Button, Paper, Collapse } from '@mui/material';
import { ReportProblemOutlined, RefreshRounded } from '@mui/icons-material';
import { useT } from '../../i18n/useT';

interface Props {
  children: React.ReactNode;
  /** Optional reporter, e.g. (err, info) => Sentry.captureException(err). */
  onError?: (error: Error, info: React.ErrorInfo) => void;
}

interface State {
  error: Error | null;
  showDetails: boolean;
}

export class AppErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null, showDetails: false };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Always log so it shows up in dev tools and PWA-debug
    // surfaces. Reporter is opt-in.
    // eslint-disable-next-line no-console
    console.error('[AppErrorBoundary] caught:', error, info);
    try {
      this.props.onError?.(error, info);
    } catch {
      /* a reporter failure must not nuke the fallback render */
    }
  }

  reset = () => {
    this.setState({ error: null, showDetails: false });
  };

  reload = () => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  toggleDetails = () => {
    this.setState((s) => ({ showDetails: !s.showDetails }));
  };

  render() {
    const { error, showDetails } = this.state;
    if (!error) return this.props.children;

    // Class components can't use hooks, so the translated fallback
    // UI lives in the functional component below.
    return (
      <ErrorFallbackView
        error={error}
        showDetails={showDetails}
        onReset={this.reset}
        onReload={this.reload}
        onToggleDetails={this.toggleDetails}
      />
    );
  }
}

const ErrorFallbackView: React.FC<{
  error: Error;
  showDetails: boolean;
  onReset: () => void;
  onReload: () => void;
  onToggleDetails: () => void;
}> = ({ error, showDetails, onReset, onReload, onToggleDetails }) => {
  const t = useT();
  return (
      <Box
        role="alert"
        sx={{
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          bgcolor: 'background.default',
        }}
      >
        <Paper
          elevation={1}
          sx={{
            maxWidth: 520,
            width: '100%',
            p: { xs: 3, sm: 4 },
            borderRadius: 3,
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              mx: 'auto',
              mb: 2,
              width: 56,
              height: 56,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              bgcolor: 'rgba(224, 122, 95, 0.12)',
              color: 'secondary.dark',
            }}
          >
            <ReportProblemOutlined fontSize="large" />
          </Box>

          <Typography
            variant="h6"
            sx={{
              mb: 1,
              fontWeight: 600,
              letterSpacing: '-0.01em',
            }}
          >
            {t('Something went wrong')}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 3, lineHeight: 1.6 }}
          >
            {t('The app hit an unexpected error. Try again, and if it keeps happening, reloading usually clears it up.')}
          </Typography>

          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              justifyContent: 'center',
              flexWrap: 'wrap',
              mb: 2,
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={onReset}
              startIcon={<RefreshRounded />}
            >
              {t('Try again')}
            </Button>
            <Button variant="outlined" color="primary" onClick={onReload}>
              {t('Reload app')}
            </Button>
          </Box>

          <Button
            size="small"
            color="inherit"
            onClick={onToggleDetails}
            sx={{ color: 'text.secondary', textTransform: 'none' }}
          >
            {showDetails ? t('Hide details') : t('Show technical details')}
          </Button>

          <Collapse in={showDetails}>
            <Box
              component="pre"
              sx={{
                mt: 2,
                p: 2,
                bgcolor: 'rgba(0,0,0,0.04)',
                borderRadius: 2,
                fontSize: 12,
                lineHeight: 1.5,
                color: 'text.secondary',
                overflow: 'auto',
                maxHeight: 240,
                textAlign: 'left',
                fontFamily:
                  'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
              }}
            >
              {String(error.message || error)}
              {error.stack ? `\n\n${error.stack}` : ''}
            </Box>
          </Collapse>
        </Paper>
      </Box>
  );
};

export default AppErrorBoundary;
